import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import PaginationControls from "../../components/Pagination/PaginationControls";
import dateFormat from "dateformat";
import { axiosConfig } from "../../axios/axiosConfig";

const InvoiceTable = () => {
  const [invoiceData, setInvoiceData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const offset = 100;

  const fetchInvoiceData = async () => {
    setTableLoading(true);
    try {
      const params = { page, deleted: showDeleted };
      if (fromDate) params.from_date = dateFormat(new Date(fromDate), "yyyy-mm-dd");
      if (toDate) params.to_date = dateFormat(new Date(toDate), "yyyy-mm-dd");
      const response = await axiosConfig({
        method: "get",
        url: "/tnibroadmin/invoices",
        params,
      });
      const apiData = response.data;
      setInvoiceData(apiData.results?.response || []);
      setTotalCount(apiData.count || 0);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceData();
  }, [page, showDeleted, fromDate, toDate]);

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  return (
    <Box
      sx={{
        background: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      }}
    >
      {/* Table header bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          background: "#04343a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
          Invoice Records
          {totalCount > 0 && (
            <Chip
              label={totalCount}
              size="small"
              sx={{ ml: 1, background: "rgba(1,101,81,0.25)", color: "#fff", fontSize: 11, height: 18, fontWeight: 700 }}
            />
          )}
        </Typography>

        {/* Filters inside bar */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
            sx={{
              background: "#fff",
              borderRadius: "6px",
              width: 145,
              "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 12 },
              "& .MuiInputLabel-root": { fontSize: 12 },
            }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
            sx={{
              background: "#fff",
              borderRadius: "6px",
              width: 145,
              "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 12 },
              "& .MuiInputLabel-root": { fontSize: 12 },
            }}
          />
          {(fromDate || toDate) && (
            <Tooltip title="Clear date filter">
              <Button
                size="small"
                onClick={handleClear}
                startIcon={<FilterAltOffIcon fontSize="small" />}
                sx={{
                  background: "#fff",
                  color: "#dc2626",
                  fontWeight: 600,
                  fontSize: 11,
                  borderRadius: "6px",
                  textTransform: "none",
                  px: 1.5,
                  "&:hover": { background: "#fef2f2" },
                }}
              >
                Clear
              </Button>
            </Tooltip>
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showDeleted}
                onChange={(e) => { setShowDeleted(e.target.checked); setPage(1); }}
                sx={{ "& .MuiSwitch-thumb": { background: showDeleted ? "#dc2626" : "#fff" } }}
              />
            }
            label={
              <Typography sx={{ fontSize: 12, color: "#fff", fontWeight: showDeleted ? 700 : 400 }}>
                Show Deleted
              </Typography>
            }
          />
        </Stack>
      </Box>

      {/* Table body */}
      {tableLoading ? (
        <Box textAlign="center" py={5}>
          <CircularProgress size={28} sx={{ color: "#016551" }} />
          <Typography sx={{ mt: 1, fontSize: 13, color: "#6b7280" }}>Loading invoices...</Typography>
        </Box>
      ) : (
        <>
          <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "65vh", borderTop: "1px solid #eef2f7" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                width: "100%",
                "& .MuiTableCell-root": {
                  padding: "7px 12px",
                  fontSize: "12px",
                  borderBottom: "1px solid #f0f4f8",
                  whiteSpace: "nowrap",
                },
                "& .MuiTableBody-root .MuiTableRow-root": {
                  "&:hover": { background: "#f0faf5" },
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      fontSize: "12px",
                      color: "#ffffff",
                      background: "#04343a",
                      padding: "12px 16px",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Invoice ID</TableCell>
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Invoice Date</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: 13, color: "#9ca3af" }}>No records found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoiceData.map((invoice, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ color: "#9ca3af", fontSize: "11px" }}>
                        {(page - 1) * offset + index + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#04343a" }}>
                        {invoice.customer_name}
                      </TableCell>
                      <TableCell sx={{ color: "#6b7280" }}>{invoice.invoice_id}</TableCell>
                      <TableCell>{invoice.invoice_number}</TableCell>
                      <TableCell>{invoice.invoice_date?.slice(0, 10)}</TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.item_name || "—"}
                          size="small"
                          sx={{
                            fontSize: "10px",
                            height: 20,
                            fontWeight: 600,
                            background: "#ede7f6",
                            color: "#4527a0",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#166534" }}>
                          ₹{invoice.item_price}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2, py: 1, borderTop: "1px solid #f0f4f8" }}>
            <PaginationControls
              totalCount={totalCount}
              page={page}
              setPage={setPage}
              offset={offset}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default InvoiceTable;
